import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Toast } from "antd-mobile";
import { CheckType, CheckItem, HazardLevel } from "@/types";
import { getHazardItems } from "@/data/hazardItems";
import { HazardGroup, BottomSheet } from "@/components";
import { v4 as uuidv4 } from "uuid";

export const CheckPage: React.FC = () => {
  const { type } = useParams<{ type: string }>();
  const navigate = useNavigate();
  const checkType = type as CheckType;

  const isStationType = [
    CheckType.LPG_DEPOT,
    CheckType.LNG_GASIFICATION,
    CheckType.LNG_FILLING,
    CheckType.GATE_PRESSURE,
  ].includes(checkType);

  const [basicInfo, setBasicInfo] = useState({
    inspectedUnit: "",
    inspectedAddress: "",
    inspector: "",
    stationManager: "",
    lastInspectionDate: "",
  });

  const [majorItems, setMajorItems] = useState<CheckItem[]>([]);
  const [generalItems, setGeneralItems] = useState<CheckItem[]>([]);
  const [checkedItems, setCheckedItems] = useState<string[]>([]);
  const [showAddSheet, setShowAddSheet] = useState(false);
  const [customItemType, setCustomItemType] = useState<HazardLevel>(
    HazardLevel.GENERAL,
  );
  const [customItemName, setCustomItemName] = useState("");

  useEffect(() => {
    const items = getHazardItems(checkType);
    setMajorItems(
      items.filter((item: CheckItem) => item.hazardLevel === HazardLevel.MAJOR),
    );
    setGeneralItems(
      items.filter(
        (item: CheckItem) => item.hazardLevel === HazardLevel.GENERAL,
      ),
    );
  }, [checkType]);

  const handleToggle = (item: CheckItem) => {
    setCheckedItems((prev) =>
      prev.includes(item.id)
        ? prev.filter((id) => id !== item.id)
        : [...prev, item.id],
    );
  };

  const handleDelete = (item: CheckItem) => {
    if (item.hazardLevel === HazardLevel.MAJOR) {
      setMajorItems((prev) => prev.filter((i) => i.id !== item.id));
    } else {
      setGeneralItems((prev) => prev.filter((i) => i.id !== item.id));
    }
    setCheckedItems((prev) => prev.filter((id) => id !== item.id));
  };

  const handleAddCustomItem = () => {
    if (!customItemName.trim()) {
      Toast.show({ content: "请输入隐患名称", icon: "fail" });
      return;
    }

    const newItem: CheckItem = {
      id: uuidv4(),
      name: customItemName.trim(),
      checkType,
      hazardLevel: customItemType,
      isCustom: true,
      createdAt: new Date(),
    };

    if (customItemType === HazardLevel.MAJOR) {
      setMajorItems((prev) => [...prev, newItem]);
    } else {
      setGeneralItems((prev) => [...prev, newItem]);
    }

    setCustomItemName("");
    setShowAddSheet(false);
    Toast.show({ content: "添加成功", icon: "success" });
  };

  const handleNext = () => {
    if (!basicInfo.inspectedUnit.trim()) {
      Toast.show({ content: "请输入被检查单位", icon: "fail" });
      return;
    }
    if (!basicInfo.inspectedAddress.trim()) {
      Toast.show({ content: "请输入检查地址", icon: "fail" });
      return;
    }
    if (!basicInfo.inspector.trim()) {
      Toast.show({ content: "请输入检查人员", icon: "fail" });
      return;
    }
    if (checkedItems.length === 0) {
      Toast.show({ content: "请至少选择一项隐患", icon: "fail" });
      return;
    }

    const allItems = [...majorItems, ...generalItems];
    const selectedItems = allItems.filter((item) =>
      checkedItems.includes(item.id),
    );

    navigate("/summary", {
      state: {
        checkType,
        basicInfo,
        selectedItems,
      },
    });
  };

  const typeTitleMap: Record<CheckType, string> = {
    [CheckType.LIQUEFIED_GAS]: "瓶装液化石油气检查",
    [CheckType.NATURAL_GAS]: "管道天然气检查",
    [CheckType.LPG_DEPOT]: "液化石油气储配站检查",
    [CheckType.LNG_GASIFICATION]: "LNG 气化站检查",
    [CheckType.LNG_FILLING]: "LNG 加气站检查",
    [CheckType.GATE_PRESSURE]: "天然气门站/调压站检查",
  };
  const typeTitle = typeTitleMap[checkType] ?? "燃气安全检查";

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* 顶部标题栏 */}
      <div className="bg-red-700 text-white px-4 py-4 sticky top-0 z-10">
        <div className="flex items-center">
          <button onClick={() => navigate(-1)} className="mr-3">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M15 18l-6-6 6-6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
          <h1 className="text-lg font-bold">{typeTitle}</h1>
        </div>
      </div>

      {/* 基本信息 */}
      <div className="bg-white p-4 mb-4">
        <h2 className="text-base font-medium text-gray-800 mb-3">基本信息</h2>
        <div className="space-y-3">
          <input
            type="text"
            placeholder="被检查单位"
            value={basicInfo.inspectedUnit}
            onChange={(e) =>
              setBasicInfo({ ...basicInfo, inspectedUnit: e.target.value })
            }
            className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:border-red-500 focus:outline-none"
          />
          <input
            type="text"
            placeholder="检查地址"
            value={basicInfo.inspectedAddress}
            onChange={(e) =>
              setBasicInfo({ ...basicInfo, inspectedAddress: e.target.value })
            }
            className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:border-red-500 focus:outline-none"
          />
          <input
            type="text"
            placeholder="检查人员"
            value={basicInfo.inspector}
            onChange={(e) =>
              setBasicInfo({ ...basicInfo, inspector: e.target.value })
            }
            className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:border-red-500 focus:outline-none"
          />
          {isStationType && (
            <>
              <input
                type="text"
                placeholder="场站负责人（可选）"
                value={basicInfo.stationManager}
                onChange={(e) =>
                  setBasicInfo({ ...basicInfo, stationManager: e.target.value })
                }
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none"
              />
              <div>
                <label className="block text-xs text-gray-500 mb-1 px-1">
                  上次检查日期（可选）
                </label>
                <input
                  type="date"
                  value={basicInfo.lastInspectionDate}
                  onChange={(e) =>
                    setBasicInfo({
                      ...basicInfo,
                      lastInspectionDate: e.target.value,
                    })
                  }
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none text-gray-700"
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* 隐患项目选择 */}
      <div className="px-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-medium text-gray-800">隐患项目</h2>
        </div>

        {/* 重大隐患 */}
        <HazardGroup
          title="重大隐患"
          level={HazardLevel.MAJOR}
          items={majorItems}
          checkedItems={checkedItems}
          onToggle={handleToggle}
          onDelete={handleDelete}
        />

        {/* 一般隐患 */}
        <HazardGroup
          title="一般隐患"
          level={HazardLevel.GENERAL}
          items={generalItems}
          checkedItems={checkedItems}
          onToggle={handleToggle}
          onDelete={handleDelete}
        />

        {/* 添加自定义项 */}
        <button
          onClick={() => setShowAddSheet(true)}
          className="w-full mt-2 py-3 flex items-center justify-center text-red-600 text-sm border border-dashed border-red-300 rounded-lg hover:bg-red-50 active:bg-red-100"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            className="mr-1"
          >
            <path
              d="M12 5v14M5 12h14"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
          添加自定义项
        </button>
      </div>

      {/* 底部操作栏 */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-600">
            已选择{" "}
            <span className="text-red-600 font-medium">
              {checkedItems.length}
            </span>{" "}
            项
          </div>
          <button
            onClick={handleNext}
            className="bg-red-600 text-white px-8 py-2.5 rounded-lg font-medium hover:bg-red-700 active:bg-red-800"
          >
            下一步
          </button>
        </div>
      </div>

      {/* 添加自定义项弹窗 */}
      <BottomSheet
        title="添加自定义隐患项"
        visible={showAddSheet}
        onClose={() => setShowAddSheet(false)}
      >
        <div className="space-y-4">
          {/* 隐患级别选择 */}
          <div>
            <label className="block text-sm text-gray-600 mb-2">隐患级别</label>
            <div className="flex gap-3">
              <button
                onClick={() => setCustomItemType(HazardLevel.MAJOR)}
                className={`flex-1 py-2.5 rounded-lg border-2 transition-colors ${
                  customItemType === HazardLevel.MAJOR
                    ? "border-red-600 bg-red-50 text-red-600"
                    : "border-gray-200 text-gray-600"
                }`}
              >
                重大隐患
              </button>
              <button
                onClick={() => setCustomItemType(HazardLevel.GENERAL)}
                className={`flex-1 py-2.5 rounded-lg border-2 transition-colors ${
                  customItemType === HazardLevel.GENERAL
                    ? "border-orange-500 bg-orange-50 text-orange-500"
                    : "border-gray-200 text-gray-600"
                }`}
              >
                一般隐患
              </button>
            </div>
          </div>

          {/* 隐患名称 */}
          <div>
            <label className="block text-sm text-gray-600 mb-2">隐患名称</label>
            <textarea
              placeholder="请输入隐患描述"
              value={customItemName}
              onChange={(e) => setCustomItemName(e.target.value)}
              rows={3}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:border-red-500 focus:outline-none resize-none"
            />
          </div>

          {/* 确认按钮 */}
          <button
            onClick={handleAddCustomItem}
            className="w-full bg-red-600 text-white py-3 rounded-lg font-medium hover:bg-red-700"
          >
            确认添加
          </button>
        </div>
      </BottomSheet>
    </div>
  );
};
